import { z } from "zod/v4";
import { defineEndpoint, defineResource, TenantPathParams } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../constants";

// ─── Get asset location (GET /asset-location/{assetId}) ──────────────────────
//
// The config/map page needs each asset's live geolocation to centre the map and
// drop asset markers. Location is a *live* per-asset enquiry against the
// commsboard diagnostics table (see `packages/a3s-asset-location`) — it is not
// persisted with the scenario config, so it gets its own endpoint rather than
// riding along on the scenario list.
//
// `null` is a first-class, expected result: many assets have no recent report
// with a usable location, and the page must handle "couldn't locate this asset"
// gracefully. We model that as `{ location: null }` rather than a 404 so the
// caller can distinguish "asset has no known location" (a normal answer) from a
// transport/permission error.

/**
 * A resolved asset geolocation. Latitude/longitude are numbers in degrees.
 * `asOf` is the ISO instant of the diagnostics report the location came from,
 * so the UI can show how fresh the fix is.
 */
export const AssetLocation = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  altitude: z.number().optional(),
  asOf: z.iso.datetime({ offset: true }).optional(),
});

export type AssetLocation = z.infer<typeof AssetLocation>;

export const GetAssetLocationResponse = z.object({
  /** `null` when the asset has no recent report carrying a usable location. */
  location: AssetLocation.nullable(),
});

export type GetAssetLocationResponse = z.infer<typeof GetAssetLocationResponse>;

const assetLocationResource = defineResource({
  feature: FEATURE_NAME,
  resource: "asset-location/{assetId}",
  resourcePathSchema: z.object({ assetId: z.string().min(1) }),
  scope: "tenant",
});

export const getAssetLocationEndpoint = defineEndpoint(assetLocationResource, {
  responseSchema: GetAssetLocationResponse,
});

export { TenantPathParams as GetAssetLocationPathParams };
